import type {
    CommerceShareChannel,
    CommerceSharePreview,
    CommerceSharePreviewItem,
    CommerceShareResource,
    CommerceShareTarget,
    CommerceStorefrontContact,
} from "@teqtank/commerce-contracts";
import { MockCommerceTransport } from "@teqtank/commerce-transport";
import type { DefaultCartController } from "../cart/register-default-cart-operations.js";
import { mockProducts } from "../products/mock-product-catalogue.js";
import { acceptCommerceShareOperation } from "../operations/accept-commerce-share-operation.js";
import { createCommerceShareResourceOperation } from "../operations/create-commerce-share-resource-operation.js";
import { resolveCommerceShareOperation } from "../operations/resolve-commerce-share-operation.js";

export function registerDefaultShareOperations(
    transport: MockCommerceTransport,
    cart: DefaultCartController,
): void {
    const previews = new Map<string, CommerceSharePreview>();
    transport.register(createCommerceShareResourceOperation, ({ target }) => {
        const resource = createResource(target);
        previews.set(resource.id, createPreview(resource, cart));
        return { share: resource };
    });
    transport.register(resolveCommerceShareOperation, ({ shareId }) => {
        const preview =
            previews.get(shareId) ??
            createPreview(
                createResource({
                    type: "routine",
                    id: shareId === "morning-stack" ? shareId : "morning-stack",
                }),
                cart,
            );
        return { preview };
    });
    transport.register(acceptCommerceShareOperation, ({ shareId, strategy }) => {
        const preview =
            previews.get(shareId) ??
            createPreview(createResource({ type: "routine", id: shareId }), cart);
        if (!preview.canAccept) throw new Error(`Share '${shareId}' cannot be added to the Cart.`);
        const updated = cart.applySharedItems(
            preview.items.map((item) => ({
                productId: item.productId,
                ...(item.variantId ? { variantId: item.variantId } : {}),
                quantity: item.quantity,
                purchaseType: item.purchaseType,
            })),
            strategy,
        );
        return { preview, cart: updated };
    });
}

function createResource(target: CommerceShareTarget): CommerceShareResource {
    const path = [target.type, target.id]
        .filter(Boolean)
        .map((value) => encodeURIComponent(value!))
        .join("/");
    const alias = target.metadata?.webAlias ?? "store";
    const id = target.id ? `${target.type}:${target.id}` : `${target.type}:${Date.now()}`;
    const url =
        target.url ??
        `https://shop.example.test/${encodeURIComponent(alias)}/${path}?share=${encodeURIComponent(id)}`;
    const title = target.title ?? titleFor(target);
    const description = target.description ?? descriptionFor(target);
    const text = `${title} — ${description}`;
    const channelUrls: Partial<Record<CommerceShareChannel, string>> = {
        email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text} ${url}`)}`,
        sms: `sms:?body=${encodeURIComponent(`${text} ${url}`)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        messenger: `fb-messenger://share/?link=${encodeURIComponent(url)}`,
        x: `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        pinterest: `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}`,
    };
    return Object.freeze({
        id,
        target,
        url,
        shortUrl: url,
        deepLink: `teqtank://share/${path}`,
        title,
        description,
        text,
        imageUrl: target.imageUrl ?? null,
        qrCodeUrl: `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(url)}`,
        channelUrls: Object.freeze(channelUrls),
        createdAt: new Date(),
        expiresAt:
            target.type === "cart" || target.type === "routine"
                ? new Date(Date.now() + 7 * 86400000)
                : null,
        metadata: Object.freeze({
            source: "mock-commerce-transport",
            ...(target.metadata ?? {}),
        }),
    });
}

function createPreview(
    resource: CommerceShareResource,
    cart: DefaultCartController,
): CommerceSharePreview {
    const items =
        resource.target.type === "cart"
            ? cart.current().lines.map((line) =>
                  Object.freeze({
                      productId: line.productId,
                      variantId: line.variantId,
                      name: line.name,
                      image: line.image,
                      quantity: line.quantity,
                      purchaseType: line.purchaseType,
                      unitPrice: line.unitPrice,
                  }),
              )
            : resource.target.type === "routine"
              ? mockProducts.slice(0, 3).map((product) => {
                    const variant =
                        product.variants.find((item) => item.id === product.defaultVariantId) ??
                        product.variants[0]!;
                    return Object.freeze({
                        productId: product.id,
                        variantId: variant.id,
                        name: product.name,
                        image: product.primaryImage,
                        quantity: 1,
                        purchaseType: "one-time" as const,
                        unitPrice: variant.offers.find(
                            (offer) => offer.purchaseType === "one-time",
                        )!.price.current,
                    });
                })
              : [];
    const subtotalAmount = items.reduce(
        (sum, item) => sum + item.unitPrice.amount * item.quantity,
        0,
    );
    const subtotal = items.length
        ? Object.freeze({
              amount: subtotalAmount,
              currencyCode: "NZD",
              formatted: `$${subtotalAmount.toFixed(2)}`,
          })
        : null;
    return Object.freeze({
        id: resource.id,
        resource,
        experience: resource.target.type === "cart" ? "shop" : null,
        acceptanceStrategies: Object.freeze(
            resource.target.type === "cart" || resource.target.type === "routine"
                ? (["merge", "replace"] as const)
                : [],
        ),
        contact: demoContact(),
        items: Object.freeze(items as CommerceSharePreviewItem[]),
        subtotal,
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        canAccept: items.length > 0,
        expiresAt: resource.expiresAt,
    });
}
function demoContact(): CommerceStorefrontContact {
    return Object.freeze({
        id: "partner-jessica",
        webAlias: "jessica",
        displayName: "Jessica Morgan",
        role: "Independent Brand Partner",
        biography: "Here to help you build a routine that fits your goals.",
        avatar: null,
        location: null,
        methods: Object.freeze({
            email: "jessica@example.test",
            phone: null,
            websiteUrl: "https://shop.example.test/jessica",
            messageUrl: null,
            vCardUrl: null,
        }),
    });
}
function titleFor(target: CommerceShareTarget): string {
    switch (target.type) {
        case "product":
            return "Take a look at this product";
        case "cart":
            return "A cart picked for you";
        case "collection":
            return "Shop this collection";
        case "routine":
            return "Your morning wellness routine";
        case "campaign":
            return "Explore this campaign";
        case "coupon":
            return `Use coupon ${target.id ?? ""}`.trim();
        case "contact":
            return "Save my contact details";
        case "page":
            return "Take a look";
        default:
            return "Shop my storefront";
    }
}
function descriptionFor(target: CommerceShareTarget): string {
    switch (target.type) {
        case "cart":
            return "Review these recommendations, then merge them into your bag or start fresh.";
        case "routine":
            return "A complete three-product routine ready to add in one step.";
        case "product":
            return "A richer product recommendation from your brand partner.";
        case "contact":
            return "Connect with your independent brand partner.";
        default:
            return "Shared through Teqtank Commerce.";
    }
}
